import { useCallback } from 'react';

interface ReactionUser {
  id: string;
  name: string;
  addedAt?: string;
}

interface Reaction {
  emoji: string;
  count: number;
  hasReacted: boolean;
  users?: ReactionUser[];
  hasMore?: boolean;
}

interface UseReactionOptimisticUpdatesProps {
  currentUserId?: string;
  currentUserName?: string;
}

export function useReactionOptimisticUpdates({
  currentUserId,
  currentUserName,
}: UseReactionOptimisticUpdatesProps) {
  const addReactionOptimistically = useCallback(
    (reactions: Reaction[], emoji: string): Reaction[] => {
      const existingReaction = reactions.find(r => r.emoji === emoji);

      if (existingReaction) {
        // Check if user has already reacted - if so, don't add again
        if (existingReaction.hasReacted) {
          return reactions; // No change, user already reacted
        }

        return reactions.map(r =>
          r.emoji === emoji
            ? {
                ...r,
                count: r.count + 1,
                hasReacted: true,
                users: [
                  {
                    id: currentUserId || '',
                    name: currentUserName || '',
                    addedAt: new Date().toISOString(),
                  },
                  ...(r.users?.filter(u => u.id !== currentUserId) || []), // Prevent duplicate users
                ],
              }
            : r
        );
      } else {
        // When adding a new emoji, we can't know the exact timeline position
        // So we append it and let the server response restore correct order
        return [
          ...reactions,
          {
            emoji,
            count: 1,
            hasReacted: true,
            users: [
              {
                id: currentUserId || '',
                name: currentUserName || '',
                addedAt: new Date().toISOString(),
              },
            ],
          },
        ];
      }
    },
    [currentUserId, currentUserName]
  );

  const toggleReactionOptimistically = useCallback(
    (reactions: Reaction[], emoji: string, hasReacted: boolean): Reaction[] => {
      if (hasReacted) {
        // Removing reaction - also remove current user from users array
        return reactions
          .map(r =>
            r.emoji === emoji
              ? {
                  ...r,
                  count: Math.max(0, r.count - 1),
                  hasReacted: false,
                  users: r.users?.filter(u => u.id !== currentUserId) || [],
                }
              : r
          )
          .filter(r => r.count > 0); // Remove reactions with 0 count
      } else {
        // Adding reaction - also add current user to users array
        const existingReaction = reactions.find(r => r.emoji === emoji);
        if (existingReaction) {
          // Check if user has already reacted - if so, don't add again
          if (existingReaction.hasReacted) {
            return reactions; // No change, user already reacted
          }

          return reactions.map(r =>
            r.emoji === emoji
              ? {
                  ...r,
                  count: r.count + 1,
                  hasReacted: true,
                  users: [
                    {
                      id: currentUserId || '',
                      name: currentUserName || '',
                      addedAt: new Date().toISOString(),
                    },
                    ...(r.users?.filter(u => u.id !== currentUserId) || []), // Prevent duplicate users
                  ],
                }
              : r
          );
        } else {
          return [
            ...reactions,
            {
              emoji,
              count: 1,
              hasReacted: true,
              users: [
                {
                  id: currentUserId || '',
                  name: currentUserName || '',
                  addedAt: new Date().toISOString(),
                },
              ],
            },
          ];
        }
      }
    },
    [currentUserId, currentUserName]
  );

  return {
    addReactionOptimistically,
    toggleReactionOptimistically,
  };
}
